import React, { useEffect, useState } from 'react';
import { OrderCardProcessedDetails } from './dashboard-utils';
import { Location } from '../../../clients/quipli-service/location/location';
import { AssignDriverRequest, Order, OrderProduct } from '../../../clients/quipli-service/order/order';
import Icon from '../../../components/icons/icon';
import { Typography } from '../../../components/typography/typography';
import { Tooltip } from '../../../components/tooltip/tooltip';
import { QuipliLink } from '../../../components/link/quipli-link';
import { ProductTable } from './product-table';
import { StatusTransitionButtonsV2 } from '../../order/status-transitions/status-transition-button-v2';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { GoogleShippingValueFormat } from '../../order/view/order-view';
import { SelectForm } from '../../../components/select-form/select-form';
import { Admin } from '../../../clients/quipli-service/admin/admin';
import { Dictionary } from 'lodash';
import { AddressCell, getClassForLabel, PaymentMethodCell } from '../../order/order-table-cells';
import '../../order/order-table-cells.scss';
import { formatPhoneNumber } from '../../../util/util';
import { DateTimePicker } from '../../../components/date-picker/date-time-picker';
import { DateTime } from 'luxon';
import { Modal } from '../../../components/modal/modal';
import { Button } from '../../../components/form/button';
import { toast } from '../../../util/toasts';
import { DispatchDirection, DispatchProgress } from '../../../clients/quipli-service/dispatch/dispatch';
import { getClassForDispatchStatusLabel, getLabelForDispatchStatus } from '../../dispatch-v2/DispatchV2';
import '../../dispatch-v2/dispatch-status-cell.scss';
import { TransitionDispatchForm } from '../../dispatch/components/transition-dispatch-form';
import { DispatchStatusTransitionButton } from '../../dispatch-v2/dispatch-status-transition-button';

export enum CardType {
  Order,
  Dispatch,
}

export type DirectionalOrder = Order & { direction: ('Coming in' | 'Going out')[] };
type OrderCardProps = {
  orderCardDetails: OrderCardProcessedDetails;
  onReload: () => void;
  location: Location | null | undefined;
  drivers: Admin[];
  unitTrackingViaUnitNum?: boolean;
  cardType?: CardType;
  dispatchStatus?: DispatchProgress;
};

export const OrderCard = (props: OrderCardProps) => {
  const {
    orderCardDetails,
    onReload,
    location,
    drivers,
    unitTrackingViaUnitNum = true,
    cardType = CardType.Order,
  } = props;
  const { getConfiguration, assignDriver, updateScheduledTime } = useAPI();

  const [expanded, setExpanded] = useState(false);
  const [showKilometers, setShowKilometers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [scheduledOpen, setScheduledOpen] = useState(false);
  const [scheduledTime, setScheduledTime] = useState<DateTime | undefined>(orderCardDetails.scheduledDate);
  const [isUpdatingScheduledTime, setIsUpdatingScheduledTime] = useState(false);

  const [isDispatchTransitionOpen, setIsDispatchTransitionOpen] = useState(false);

  const isGoingOut = orderCardDetails.direction.includes('Going out');
  const isComingIn = orderCardDetails.direction.includes('Coming in');

  const fmt = Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: location?.currency || 'USD',
  });

  useEffect(() => {
    const getKilometers = async () => {
      const showKilometers = (await getConfiguration('CONF_CALCULATE_DELIVERY_USING_KM_GOOGLE_API'))?.value === '1';
      setShowKilometers(showKilometers);
    };

    getKilometers();
  }, []);

  const updateScheduledTimeMethod = async (selectedDateTime: DateTime) => {
    try {
      setIsUpdatingScheduledTime(true);
      const updateScheduledBody = {
        orderId: orderCardDetails.order.id,
        orderProductIds: {
          pickup: orderCardDetails.direction.includes('Coming in')
            ? orderCardDetails.order.products.map((p) => p.id)
            : [],
          delivery: orderCardDetails.direction.includes('Going out')
            ? orderCardDetails.order.products.map((p) => p.id)
            : [],
        },
        scheduledTime: selectedDateTime,
      };

      await updateScheduledTime(orderCardDetails.order.id, updateScheduledBody);
      toast('Scheduled time updated', 'success');

      await onReload();

      setScheduledOpen(false);
    } catch {
      toast('Failed to update scheduled time', 'error');
    } finally {
      setIsUpdatingScheduledTime(false);
    }
  };

  const assignDriverMethod = async (driver: Admin | null) => {
    const products: Dictionary<OrderProduct[]> = {
      delivery: orderCardDetails.direction.includes('Going out')
        ? orderCardDetails.order.products.filter((p) => !p.shippingType.includes('Pick'))
        : [],
      pickup: orderCardDetails.direction.includes('Coming in')
        ? orderCardDetails.order.products.filter((p) => !p.shippingType.includes('Pick'))
        : [],
    };

    setIsLoading(true);
    try {
      const body: AssignDriverRequest = {
        orderId: orderCardDetails.order.id,
        orderProductIds: {
          delivery: products.delivery?.length ? products.delivery.map((p) => p.id) : undefined,
          pickup: products.pickup?.length ? products.pickup.map((p) => p.id) : undefined,
        },
        driverId: driver?.adminId,
      };

      await assignDriver(orderCardDetails.order.id, body);

      await onReload();
    } finally {
      setIsLoading(false);
    }
  };

  /*
    This is a requirement for allowing the cards to also be used for dispatch.
    For dispatch, we only want to show the going in or going out time, not both (based on its direction).
    We also want to show the scheduled time assignment thing for dispatch only, so moving this logic up a little
  */
  const DetailItemsToShow = () => {
    // Show both for orders
    if (cardType == CardType.Order) {
      return (
        <>
          <DetailItem
            title="Rental start"
            content={
              <Typography
                type="text-sm"
                weight={isGoingOut ? 'semi-bold' : 'regular'}
                className={isGoingOut ? 'text-success-600' : ''}
              >
                {orderCardDetails.rentalStartDate && orderCardDetails.rentalStartDate != null
                  ? orderCardDetails.rentalStartDate.toFormat('MMM-dd · hh:mm a')
                  : '-'}
              </Typography>
            }
          />
          <DetailItem
            title="Rental end"
            content={
              <Typography
                type="text-sm"
                weight={isComingIn ? 'semi-bold' : 'regular'}
                className={`${isComingIn ? 'text-success-600' : ''}`}
              >
                {orderCardDetails.rentalEndDate && orderCardDetails.rentalEndDate != null
                  ? orderCardDetails.rentalEndDate.toFormat('MMM-dd · hh:mm a')
                  : '-'}
              </Typography>
            }
          />
          <DetailItem
            title="Customer phone #"
            content={
              <a href={`tel:${orderCardDetails.customerPhone}`}>
                <Typography type="text-sm" weight="regular" className="text-[var(--grey-700)] underline">
                  {formatPhoneNumber(orderCardDetails.customerPhone)}
                </Typography>
              </a>
            }
          />
        </>
      );
    } else {
      return (
        <>
          {orderCardDetails.direction.includes('Going out') && (
            <DetailItem
              title="Rental start"
              content={
                <Typography
                  type="text-sm"
                  weight={isGoingOut ? 'semi-bold' : 'regular'}
                  className={isGoingOut ? 'text-success-600' : ''}
                >
                  {orderCardDetails.rentalStartDate && orderCardDetails.rentalStartDate != null
                    ? orderCardDetails.rentalStartDate.toFormat('MMM-dd · hh:mm a')
                    : '-'}
                </Typography>
              }
            />
          )}
          {orderCardDetails.direction.includes('Coming in') && (
            <DetailItem
              title="Rental end"
              content={
                <Typography
                  type="text-sm"
                  weight={isComingIn ? 'semi-bold' : 'regular'}
                  className={`${isComingIn ? 'text-success-600' : ''}`}
                >
                  {orderCardDetails.rentalEndDate && orderCardDetails.rentalEndDate != null
                    ? orderCardDetails.rentalEndDate.toFormat('MMM-dd · hh:mm a')
                    : '-'}
                </Typography>
              }
            />
          )}
          <DetailItem
            title="Scheduled time"
            content={
              <div className="cursor-pointer" onClick={() => setScheduledOpen(true)}>
                <Typography type="text-sm" weight={'regular'} className="underline">
                  {orderCardDetails.scheduledDate ? orderCardDetails.scheduledDate.toFormat('MMM-dd · hh:mm a') : '-'}
                </Typography>
              </div>
            }
          />
          <DetailItem
            title="Customer phone #"
            content={
              <a href={`tel:${orderCardDetails.customerPhone}`}>
                <Typography type="text-sm" weight="regular" className="text-[var(--grey-700)] underline">
                  {formatPhoneNumber(orderCardDetails.customerPhone)}
                </Typography>
              </a>
            }
          />
        </>
      );
    }
  };

  return (
    <div className="order-card bg-white m-2 mt-0 p-3">
      <div className="card-header flex flex-row justify-between items-center quipli-v2">
        {/* Top left direction indicator */}
        <Modal
          isOpen={scheduledOpen}
          title="Update Scheduled Time"
          close={() => setScheduledOpen(false)}
          actions={
            <>
              <Button
                className="button button-secondary"
                disabled={isUpdatingScheduledTime}
                onClick={() => setScheduledOpen(false)}
              >
                Cancel
              </Button>
              <Button
                className="button button-yellow"
                disabled={isUpdatingScheduledTime}
                onClick={() => updateScheduledTimeMethod(scheduledTime!)}
              >
                Save
              </Button>
            </>
          }
        >
          <div className="h-[50vh] w-auto">
            <DateTimePicker
              label="Scheduled time"
              timezone={location?.timezone}
              clearable={!isUpdatingScheduledTime}
              value={scheduledTime}
              onChange={setScheduledTime}
            />
          </div>
        </Modal>
        <Modal
          isOpen={isDispatchTransitionOpen}
          title="Transition Dispatch"
          close={async () => {
            setIsDispatchTransitionOpen(false);
          }}
        >
          <TransitionDispatchForm
            direction={isGoingOut ? DispatchDirection.DELIVERY : DispatchDirection.PICKUP}
            orderId={orderCardDetails.order.id}
            update={async () => {
              onReload();
            }}
          />
        </Modal>
        <div className="flex flex-row gap-2">
          {isGoingOut && (
            <div className="direction flex flex-row rounded-lg p-1 bg-quipli-yellow-25 pr-2">
              <Icon icon="ArrowUp" size="small" color="var(--warning-600)" />
              <Typography type="text-sm" weight="semi-bold" className="text-warning-600">
                Going out
              </Typography>
            </div>
          )}

          {isComingIn && (
            <div className="direction flex flex-row rounded-lg p-1 bg-grey-100 pr-2">
              <Icon icon="ArrowDown" size="small" color="var(--grey-500)" />
              <Typography type="text-sm" weight="semi-bold" className="text-[var(--grey-700)]">
                Coming in
              </Typography>
            </div>
          )}
        </div>

        {/* Top right statuses */}
        <div className="status flex flex-row gap-2 items-center quipli-v2">
          {orderCardDetails.dispatchStatus && (
            <div
              className={`rounded-16 p-2 dispatch-status-cell ${getClassForDispatchStatusLabel(
                orderCardDetails.dispatchStatus,
              )}`}
            >
              <Typography type="text-sm" weight="semi-bold" className="text-[var(--grey-700)] ">
                {getLabelForDispatchStatus(
                  orderCardDetails.dispatchStatus,
                  orderCardDetails.direction.includes('Going out')
                    ? DispatchDirection.DELIVERY
                    : DispatchDirection.PICKUP,
                )}
              </Typography>
            </div>
          )}
          <div className={`rounded-16 p-2 order-status-cell ${getClassForLabel(orderCardDetails.orderStatus)}`}>
            <Typography type="text-sm" weight="semi-bold" className="text-[var(--grey-700)] ">
              {orderCardDetails.orderStatus}
            </Typography>
          </div>
          <div className="status-icons flex flex-row mt-1">
            {orderCardDetails.isPaid && (
              <Tooltip tooltip="Paid" type="black">
                <Icon icon="Paid" size="small" color="var(--success-500)" className="ml-1" />{' '}
              </Tooltip>
            )}
            {!orderCardDetails.isPaid && (
              <Tooltip tooltip="Payable" type="black">
                <Icon icon="Unpaid" size="small" color="var(--error-500)" className="ml-1" />{' '}
              </Tooltip>
            )}
            {orderCardDetails.overDueAmount && (
              <Tooltip tooltip={orderCardDetails.overDueAmount} type="black">
                <Icon
                  icon="Warning"
                  title={orderCardDetails.overDueAmount}
                  size="small"
                  height="22"
                  width="22"
                  color={`${orderCardDetails.overDueGreater2Hours ? 'var(--error-500)' : 'var(--warning-500)'}`}
                  className="ml-1"
                />
              </Tooltip>
            )}
            {orderCardDetails.isOnAccount && (
              <Tooltip tooltip="On account" type="black">
                <Icon icon="Account" size="small" height="22" width="22" className="ml-1" title="On account" />
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {/* Customer/Location info */}
      <div className="customer-loc-info mb-2 mt-2">
        <Typography type="text-md" weight="semi-bold" className="text-[var(--grey-900)]">
          {orderCardDetails.customerName}
        </Typography>
        {orderCardDetails.order.authorizedRepresentative && (
          <Typography type="text-xs" weight="regular" className="text-[var(--grey-700)]">
            Rep: {orderCardDetails.order.authorizedRepresentative.name}
          </Typography>
        )}
        <QuipliLink to={`orders/view/${orderCardDetails.order.id}`}>
          <Typography type="text-sm" weight="regular" className="text-[var(--grey-700)] cursor-pointer underline">
            {orderCardDetails.orderNumber} | {orderCardDetails.location}
          </Typography>
        </QuipliLink>
      </div>

      {/* product details */}
      <ProductTable
        orderProducts={orderCardDetails.orderProducts}
        unitTrackingViaUnitNum={unitTrackingViaUnitNum}
        jobs={orderCardDetails.jobs}
      />

      {/* order dates */}
      <div className="order-dates grid grid-cols-4 gap-2">
        <DetailItemsToShow />

        {orderCardDetails.order.authorizedRepresentative ? (
          <DetailItem
            title="Rep. phone #"
            content={
              <a href={`tel:${orderCardDetails.order.authorizedRepresentative.phone}`}>
                <Typography type="text-sm" weight="regular" className="text-[var(--grey-700)] underline">
                  {formatPhoneNumber(orderCardDetails.order.authorizedRepresentative.phone)}
                </Typography>
              </a>
            }
          />
        ) : (
          <div></div>
        )}

        {/* TODO: add when we figure out delivery strategy */}
        {/* {orderCardDetails.isDelivery && (
              <DetailItem
                title="Delivery time"
                content={
                  <Typography type="text-sm" weight="regular" className="text-[var(--grey-700)] underline">
                    {orderCardDetails.deliveryTime}
                  </Typography>
                }
              />
            )} */}
        {expanded && (
          <>
            <DetailItem
              title="Payment status"
              content={
                <div
                  className={`rounded-16 ${
                    orderCardDetails.isPaid ? 'bg-success-50' : 'bg-error-50'
                  } w-[fit-content] p-1`}
                >
                  <Typography
                    type="text-sm"
                    weight="regular"
                    className={`text-${orderCardDetails.isPaid ? 'success-700' : 'error-700'} w-[fit-content]`}
                  >
                    {orderCardDetails.isPaid ? 'Paid' : 'Unpaid'}
                  </Typography>
                </div>
              }
            />
            <DetailItem
              title="Total"
              content={
                <Typography type="text-sm" weight="regular" className="text-[var(--grey-700)]">
                  {fmt.format(orderCardDetails.orderTotal)}
                </Typography>
              }
            />
            <DetailItem
              title="Payment method"
              content={
                <Typography type="text-sm" weight="regular" className="text-[var(--grey-700)]">
                  <PaymentMethodCell order={orderCardDetails.order} />
                </Typography>
              }
            />
            <DetailItem
              title="Balance"
              content={
                <Typography type="text-sm" weight="regular" className="text-[var(--grey-700)]">
                  {fmt.format(orderCardDetails.orderBalance)}
                </Typography>
              }
            />
          </>
        )}
      </div>

      {/* more details */}
      <div className="more-details mt-2">
        {/* shipping address */}
        {((expanded && orderCardDetails.isDelivery) || cardType === CardType.Dispatch) && (
          <DetailItem
            title="Delivery address"
            content={
              <div className="flex flex-row gap-1">
                <Typography type="text-sm" weight="regular" className="text-[var(--grey-700)] underline">
                  <AddressCell address={orderCardDetails.order.shippingAddress} />
                </Typography>
                {orderCardDetails.order.googleShippingCalculation && (
                  <Typography type="text-sm" className="text-[var(--grey-500)] flex flex-row">
                    (
                    <GoogleShippingValueFormat
                      order={orderCardDetails.order}
                      isKilometers={showKilometers ?? false}
                      className="text-[var(--grey-500)]"
                    />
                    )
                  </Typography>
                )}
              </div>
            }
          />
        )}
        {cardType === CardType.Order && (
          <div onClick={() => setExpanded((old) => !old)} className="cursor-pointer mt-2">
            <Typography type="text-sm" className="underline">
              {expanded ? '- Less details' : '+ More details'}
            </Typography>
          </div>
        )}
      </div>

      {/* TODO: figure out how we're going to do delivery drivers */}
      {/* Driver assignment for delivery */}
      <div>
        {orderCardDetails.isDelivery && (
          <div className="driver-assignment grid grid-cols-2 gap-2 mt-2 mb-1">
            <DetailItem
              title="Delivery driver"
              content={
                <SelectForm
                  placeholder={'Assign driver'}
                  value={
                    orderCardDetails.direction.includes('Going out')
                      ? orderCardDetails.deliveryDriverId
                        ? drivers.find((d) => d.adminId === orderCardDetails.deliveryDriverId)
                        : null
                      : orderCardDetails.pickupDriverId
                      ? drivers.find((d) => d.adminId === orderCardDetails.pickupDriverId)
                      : null
                  }
                  isClearable={true}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  options={drivers}
                  getOptionLabel={(d: Admin) => d.adminFullName}
                  getOptionValue={(d: Admin) => d.adminId.toString()}
                  onChange={(d: Admin | null) => assignDriverMethod(d)}
                  isDisabled={isLoading}
                  menuPortalTarget={document.body}
                  menuPosition="absolute"
                />
              }
            />
          </div>
        )}
      </div>

      {/* Status CTA */}
      {cardType === CardType.Order && (
        <div className="status-cta w-full flex justify-center items-center mt-2">
          <StatusTransitionButtonsV2
            order={orderCardDetails.order}
            location={orderCardDetails.fullLocation!}
            isDisabled={false}
            reload={onReload}
            hideTransitions={['reject', 'reopen']}
          />
        </div>
      )}
      {cardType !== CardType.Order &&
        orderCardDetails.order.dispatches?.length &&
        orderCardDetails.order.documentType == 'Order' && (
          <div className="status-cta w-full flex justify-start items-start mt-2">
            <DispatchStatusTransitionButton
              orderDispatch={orderCardDetails.order.dispatches!.find((_) => _)!}
              reload={onReload}
              direction={
                orderCardDetails.direction.includes('Going out') ? DispatchDirection.DELIVERY : DispatchDirection.PICKUP
              }
            />
          </div>
        )}
    </div>
  );
};

const DetailItem = ({ title, content }: { title: string; content: React.ReactNode }) => (
  <div>
    <Typography type="text-xs" weight="regular" className="text-grey-500">
      {title}
    </Typography>
    {content}
  </div>
);
